import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ChevronDown, Building2 } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { ROUTES, servicePath } from "@/lib/routes";
import SiteLogo from "./SiteLogo";
import FooterSocialLinks from "./FooterSocialLinks";

function FooterCollapsible({ title, children, testId }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="border-b border-[#fbd9e5]/10 lg:border-0" data-testid={testId}>
      <button
        type="button"
        className="flex w-full items-center justify-between gap-3 py-4 text-left lg:pointer-events-none lg:cursor-default lg:py-0"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span className="text-[10px] font-semibold uppercase tracking-[0.25em] text-[#fbd9e5]/45">
          {title}
        </span>
        <ChevronDown
          className={`h-4 w-4 shrink-0 text-[#fbd9e5]/50 transition-transform duration-300 lg:hidden ${
            open ? "rotate-180" : ""
          }`}
          aria-hidden
        />
      </button>
      <div
        className={`grid transition-[grid-template-rows] duration-300 ease-out lg:!grid-rows-[1fr] ${
          open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
        }`}
      >
        <div className="overflow-hidden lg:overflow-visible">
          <div className="pb-4 lg:mt-3 lg:pb-0">{children}</div>
        </div>
      </div>
    </div>
  );
}

function FooterLinkList({ items, horizontal = false }) {
  return (
    <ul
      className={
        horizontal
          ? "flex flex-wrap items-center justify-end gap-x-5 gap-y-2 text-sm text-[#fbd9e5]/80"
          : "space-y-2.5 text-sm text-[#fbd9e5]/85"
      }
    >
      {items.map((item) => (
        <li key={item.to}>
          <Link
            to={item.to}
            className={`transition-colors hover:text-white ${horizontal ? "whitespace-nowrap" : "inline-block"}`}
          >
            {item.label}
          </Link>
        </li>
      ))}
    </ul>
  );
}

export default function Footer() {
  const { t, contactInfo } = useLanguage();
  const year = new Date().getFullYear();

  const exploreLinks = [
    { to: ROUTES.home, label: t.nav.home },
    { to: ROUTES.services, label: t.nav.services },
    { to: ROUTES.articles, label: t.nav.articles },
    { to: ROUTES.portfolio, label: t.nav.portfolio },
    { to: ROUTES.about, label: t.nav.about },
    { to: ROUTES.contact, label: t.nav.contact },
  ];

  const serviceLinks = (t.footer.service_links || []).map((s) => ({
    to: servicePath(s.slug),
    label: s.label,
  }));

  const policyLinks = [
    { to: ROUTES.privacy, label: t.footer.privacy_link },
    { to: ROUTES.terms, label: t.footer.terms_link },
    { to: ROUTES.cookies, label: t.footer.cookies_link },
  ];

  const companyName = contactInfo.companyName?.trim();
  const companyCui = contactInfo.companyCui?.trim();

  return (
    <footer
      className="relative overflow-hidden border-t border-[#fbd9e5]/10 bg-[#3e2723] text-[#fbd9e5]"
      data-testid="footer"
    >
      <div aria-hidden className="pointer-events-none absolute -top-24 right-0 h-48 w-48 rounded-full bg-[#fbd9e5]/8 blur-3xl" />

      <div className="relative mx-auto max-w-7xl px-6 py-10 sm:px-10 lg:px-16 lg:py-14">
        <div className="grid gap-8 lg:grid-cols-12 lg:gap-10 lg:items-start">
          <div className="lg:col-span-4">
            <Link to={ROUTES.home} className="inline-flex max-w-full items-center">
              <SiteLogo variant="footer" />
            </Link>

            {(companyName || companyCui) && (
              <div
                className="mt-5 flex gap-3 rounded-2xl border border-[#fbd9e5]/12 bg-[#fbd9e5]/[0.06] px-4 py-3.5"
                data-testid="footer-company-info"
              >
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#fbd9e5]/10 text-[#fbd9e5]/80">
                  <Building2 className="h-4 w-4" aria-hidden />
                </div>
                <div className="min-w-0 text-xs leading-relaxed">
                  {companyName ? <p className="font-semibold text-[#fbd9e5]/95">{companyName}</p> : null}
                  {companyCui ? <p className="mt-0.5 text-[#fbd9e5]/55">CUI {companyCui}</p> : null}
                </div>
              </div>
            )}

            <FooterSocialLinks contactInfo={contactInfo} />
          </div>

          <div className="lg:col-span-5 lg:grid lg:grid-cols-2 lg:gap-8">
            <FooterCollapsible title={t.footer.explore} testId="footer-explore">
              <FooterLinkList items={exploreLinks} />
            </FooterCollapsible>
            <FooterCollapsible title={t.footer.services_title} testId="footer-services">
              <FooterLinkList items={serviceLinks} />
            </FooterCollapsible>
          </div>

          <div className="lg:col-span-3">
            <FooterCollapsible title={t.footer.contact_title} testId="footer-contact">
              <ul className="space-y-3 text-sm">
                <li>
                  <a
                    href={`mailto:${contactInfo.email}`}
                    className="inline-flex items-center gap-1 text-[#fbd9e5]/90 hover:text-white"
                  >
                    {contactInfo.email}
                    <ArrowUpRight className="h-3.5 w-3.5 opacity-70" />
                  </a>
                </li>
                <li className="text-[#fbd9e5]/75">{contactInfo.phone}</li>
                <li className="text-xs leading-relaxed text-[#fbd9e5]/65">{t.contact.location_value}</li>
              </ul>
            </FooterCollapsible>

            <div className="lg:hidden">
              <FooterCollapsible title={t.footer.policies_title} testId="footer-policies">
                <FooterLinkList items={policyLinks} />
              </FooterCollapsible>
            </div>
          </div>
        </div>

        <div className="mt-10 border-t border-[#fbd9e5]/10 pt-6">
          <nav className="mb-5 hidden lg:block" aria-label={t.footer.policies_title}>
            <FooterLinkList items={policyLinks} horizontal />
          </nav>
          <div className="flex flex-col gap-3 text-[11px] text-[#fbd9e5]/45 sm:flex-row sm:items-center sm:justify-between">
            <p>
              © {year} Social Destiny · {t.footer.rights}
            </p>
            <p className="sm:text-right">{t.footer.made_with}</p>
          </div>
        </div>
      </div>
    </footer>
  );
}
